import React, { useState, useRef, useEffect } from 'react';
import { AlertTriangle, ArrowLeft, Check, Fingerprint, KeyRound, Loader2, Mail, ScanFace, Terminal } from 'lucide-react';
import { sendSignInOtp, verifySignInOtp, devSignIn, DEV_AUTH_AVAILABLE } from '../lib/backOfficeApi';
import {
  cancelCeremony, clearPasskeyHint, enrollPasskey, isAlreadyEnrolled, isCancellation,
  passkeyLabel, passkeySupport, readPasskeyHint, savePasskeyHint, signInWithPasskey,
} from '../lib/passkey';
import { BRAND } from '../lib/brand';

// Email-OTP sign-in for the back office, against the same better-auth backend
// tkt-frontend uses: enter email → receive a one-time code → enter the code.
// Shown by App.jsx when there is no active session; `onSignedIn` enters the app.
// The last email that actually completed a sign-in. Browser autofill alone can't be
// relied on here: this flow has no password field, so nothing is ever offered to the
// credential store, and contact-info autofill is a per-browser setting we don't own.
// An operator who signs in daily should never retype their own address.
const LAST_EMAIL_KEY = 'tkt.backoffice.lastSignInEmail';
const readLastEmail = () => { try { return localStorage.getItem(LAST_EMAIL_KEY) || ''; } catch { return ''; } };
export const rememberSignInEmail = (value) => {
  const email = String(value || '').trim();
  if (!email) return;
  try { localStorage.setItem(LAST_EMAIL_KEY, email); }
  catch { /* private mode / storage disabled — prefill is a convenience, never a blocker */ }
};

export const SignInPage = ({ onSignedIn }) => {
  const [step, setStep] = useState('email'); // 'email' | 'otp'
  const [email, setEmail] = useState(readLastEmail);
  const [otp, setOtp] = useState('');
  const [busy, setBusy] = useState(null); // 'send' | 'verify' | 'dev' | 'resend'
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const otpRef = useRef(null);

  // ── Passkey ────────────────────────────────────────────────────────────────
  const [pk, setPk] = useState({ supported: false, platform: false, conditional: false });
  // 'awaiting' (the OS prompt is up) | 'done'. Only two, because the plugin owns the
  // ceremony and reports no intermediate progress — inventing "Preparing…"/"Verifying…"
  // states it never emits would be narrating a wait we cannot actually see.
  const [pkStage, setPkStage] = useState(null);
  const [hint] = useState(readPasskeyHint);           // frozen at mount: see below
  const [offerEnroll, setOfferEnroll] = useState(false);
  const label = React.useMemo(() => passkeyLabel(), []);
  const PkIcon = /face/i.test(label) ? ScanFace : Fingerprint;
  const pkBusy = pkStage === 'awaiting';

  useEffect(() => { if (step === 'otp') otpRef.current?.focus(); }, [step]);

  useEffect(() => { let alive = true; passkeySupport().then((s) => alive && setPk(s)); return () => { alive = false; }; }, []);

  const completeWithPasskey = async (user) => {
    setPkStage('done');
    if (user?.email) savePasskeyHint(user.email);
    await onSignedIn();
  };

  // THE FRICTIONLESS PATH: a conditional ceremony runs from the moment the form is on
  // screen, so the operator's passkey is simply THERE in the email field's own dropdown.
  // No button to notice, no prompt fired at someone who has none, and — the point — no
  // dependence on our localStorage hint: the browser knows what credentials exist, and this
  // is what still works after cleared storage, in a second browser profile, or with a
  // passkey synced from another device. It stays pending for as long as the field is
  // available, so it is aborted on unmount and before any explicit attempt.
  useEffect(() => {
    if (step !== 'email' || !pk.supported || !pk.conditional || pkBusy) return;
    let live = true;
    (async () => {
      try {
        const user = await signInWithPasskey({ conditional: true });
        if (live) await completeWithPasskey(user);
      } catch {
        // Silent by design. Nobody asked for this; it must never produce an error the
        // operator has to dismiss on a form they were about to fill in anyway.
      }
    })();
    // WebAuthn allows one outstanding request per page, so leaving this pending would break
    // the explicit button and any later ceremony. Cancelled on the way out, not left running.
    return () => { live = false; cancelCeremony(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, pk.supported, pk.conditional]);

  const signInWithDevice = async () => {
    if (busy || pkBusy) return;
    setError('');
    setPkStage('awaiting');              // the OS prompt is the whole wait; nothing to stage before it
    try {
      // signInWithPasskey cancels the background conditional ceremony first — one
      // outstanding WebAuthn request per page, and this tap is the one that matters.
      const user = await signInWithPasskey();
      await completeWithPasskey(user);
    } catch (err) {
      setPkStage(null);
      // A DELIBERATE TAP ALWAYS GETS AN ANSWER, even when the answer is only "that didn't
      // work". Silence is tempting here because a dismissed prompt is the commonest outcome
      // and does not deserve a red box — but the plugin collapses EVERY ceremony failure
      // into one `AUTH_CANCELLED`, so "you dismissed it" and "this can never work on this
      // device" are indistinguishable (verified: a NotAllowedError arrives as AUTH_CANCELLED,
      // status 400). Staying quiet on that leaves an operator tapping a dead button forever.
      // So the copy is neutral enough to sit under a cancellation and useful enough to
      // rescue a real failure — and it points at the code form already below it.
      setInfo('');
      setError(`Couldn't confirm with ${label}. You can use a sign-in code below.`);
      // The hint is only dropped when the account genuinely has no passkey for us any more.
      // Clearing it on a mere cancellation would retire the button the operator uses daily
      // because they once hit escape.
      if (!isCancellation(err)) clearPasskeyHint();
    }
  };

  const enrollThisDevice = async () => {
    if (busy) return;
    setError(''); setBusy('enroll');
    try {
      await enrollPasskey();
      savePasskeyHint(email);
      setOfferEnroll(false);
      await onSignedIn();
    } catch (err) {
      // Cancelled, or the platform refusing a duplicate it already holds — both mean
      // "carry on into the app", neither is worth a red box on a successful sign-in.
      if (isCancellation(err) || isAlreadyEnrolled(err)) {
        if (isAlreadyEnrolled(err)) savePasskeyHint(email);
        setOfferEnroll(false);
        await onSignedIn();
        return;
      }
      setError(`Could not set up ${label}. You are signed in — you can try again later.`);
    } finally { setBusy(null); }
  };

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (busy || !email.trim()) return;
    setError(''); setInfo(''); setBusy('send');
    try {
      await sendSignInOtp({ email });
      setInfo(`We sent a sign-in code to ${email.trim()}.`);
      setStep('otp');
    } catch (err) { setError(err?.message || 'Could not send code'); }
    finally { setBusy(null); }
  };

  const resend = async () => {
    if (busy) return;
    setError(''); setBusy('resend');
    try { await sendSignInOtp({ email }); setInfo('A new code is on its way.'); }
    catch (err) { setError(err?.message || 'Could not resend code'); }
    finally { setBusy(null); }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    if (busy || otp.trim().length < 4) return;
    setError(''); setBusy('verify');
    try {
      await verifySignInOtp({ email, otp });
      // Only a VERIFIED address is worth remembering — a typo that never received a
      // code would otherwise be prefilled forever. (refreshSession also records it
      // from the session; this covers the moment before that resolves.)
      rememberSignInEmail(email);
      // The ONE moment offering a passkey costs nothing: they are already here and already
      // verified, and the offer is "never do this again". Asked on a device that can
      // actually do it, and never asked twice. Everyone else goes straight in, as before.
      if (pk.platform && !readPasskeyHint()) { setOfferEnroll(true); return; }
      await onSignedIn();
    } catch (err) { setError(err?.message || 'Invalid or expired code'); }
    finally { setBusy(null); }
  };

  const devLogin = async () => {
    if (busy) return;
    setError(''); setBusy('dev');
    try { await devSignIn(); await onSignedIn(); }
    catch (err) { setError(err?.message || 'Developer sign-in failed'); }
    finally { setBusy(null); }
  };

  const inputCls = 'w-full h-10 bg-white border border-[#E6E4E1] rounded-md px-3 text-[13px] text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#9CA3AF] focus:ring-2 focus:ring-[#1F2937]/5';
  const primaryBtn = (disabled) => `w-full h-10 rounded-md text-[13px] font-medium inline-flex items-center justify-center gap-2 ${disabled ? 'bg-[#E6E4E1] text-[#9CA3AF] cursor-not-allowed' : 'bg-[#1F2937] hover:bg-[#111827] text-white'}`;

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#FBFAF7] px-4">
      <div className="w-full max-w-[400px]">
        <div className="flex items-center gap-2.5 mb-6 justify-center">
          {/* The sign-in card is the one slot with real horizontal room, so a brand that
              ships a wordmark shows it here at full width instead of being letterboxed
              into a 36px tile. Brands without one keep the square mark, unchanged. */}
          {BRAND.wordmark
            ? <img src={BRAND.wordmark} alt={BRAND.name} className="h-7 w-auto object-contain" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            : <img src={BRAND.logo} alt={BRAND.name} className="w-9 h-9 rounded-md object-contain bg-white" onError={(e) => { e.currentTarget.style.display = 'none'; }} />}
          <div>
            <div className="text-[15px] font-semibold text-[#1F2937] leading-tight">Back Office</div>
            <div className="text-[11px] text-[#6B7280]">Internal Platform Admin</div>
          </div>
        </div>

        <div className="bg-white border border-[#E6E4E1] rounded-[12px] px-6 py-7 shadow-sm">
          {/* Signed in already — the only thing left is whether they want to skip the code
              next time. Its own state, so it can never be mistaken for a gate: both buttons
              lead into the console, and the wording says the sign-in already worked. */}
          {offerEnroll ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <PkIcon className="w-4 h-4 text-[#4B5563]" strokeWidth={1.75} />
                <h1 className="text-[16px] font-semibold text-[#1F2937]">You're signed in</h1>
              </div>
              <p className="text-[12.5px] text-[#6B7280] mb-5">Use {label} next time and skip the email code. Your {label.toLowerCase() === 'windows hello' ? 'PIN or biometric' : 'fingerprint or face'} never leaves this device.</p>
              {error && <ErrorBox>{error}</ErrorBox>}
              <div className="space-y-2">
                <button onClick={enrollThisDevice} disabled={!!busy} className={primaryBtn(!!busy)}>
                  {busy === 'enroll' ? <Loader2 className="w-4 h-4 animate-spin" /> : <PkIcon className="w-4 h-4" />}
                  {busy === 'enroll' ? 'Setting up…' : `Set up ${label}`}
                </button>
                <button onClick={async () => { setOfferEnroll(false); await onSignedIn(); }} disabled={!!busy}
                  className="w-full h-9 rounded-md text-[12.5px] font-medium border border-[#E6E4E1] bg-white hover:bg-[#FBFAF7] text-[#4B5563] disabled:opacity-50">
                  Not now
                </button>
              </div>
            </>
          ) : step === 'email' ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <Mail className="w-4 h-4 text-[#4B5563]" strokeWidth={1.75} />
                <h1 className="text-[16px] font-semibold text-[#1F2937]">Sign in</h1>
              </div>
                <p className="text-[12.5px] text-[#6B7280] mb-5">
                  {/* Enter your TKT email and we'll send you a one-time sign-in code. Access is restricted to authorized operators.*/}
                </p>

              {error && <ErrorBox>{error}</ErrorBox>}

              {/* Offered only to a device that has ALREADY set one up. Showing "Sign in with
                  Touch ID" to someone who never enrolled is a button that can only fail —
                  they get a prompt, then "no passkey", then the form they started at. Those
                  operators are served silently by the autofill dropdown instead. */}
              {hint && pk.platform && (
                <div className="mb-4">
                  <button type="button" onClick={signInWithDevice} disabled={pkBusy || !!busy}
                    className={`w-full h-10 rounded-md text-[13px] font-medium inline-flex items-center justify-center gap-2 ${pkBusy ? 'bg-[#111827] text-white/80 cursor-wait' : 'bg-[#1F2937] hover:bg-[#111827] text-white'}`}>
                    {pkStage === 'done' ? <Check className="w-4 h-4" /> : pkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <PkIcon className="w-4 h-4" />}
                    {/* The stages are the ceremony's REAL ones, so the wait reads as movement
                        rather than a spinner that might be stuck. */}
                    {pkStage === 'done' ? 'Signed in' : pkStage === 'awaiting' ? `Confirm with ${label}` : `Sign in with ${label}`}
                  </button>
                  <div className="text-[11px] text-[#9CA3AF] text-center mt-1.5 truncate">{hint}</div>
                  <div className="flex items-center gap-3 mt-4"><div className="h-px flex-1 bg-[#EEEDEA]" /><span className="text-[10.5px] uppercase tracking-[0.06em] text-[#9CA3AF]">or use a code</span><div className="h-px flex-1 bg-[#EEEDEA]" /></div>
                </div>
              )}

              <form onSubmit={sendCode} className="space-y-3">
                <div>
                  <label htmlFor="signin-email" className="block text-[11.5px] font-medium text-[#1F2937] mb-1.5">Email</label>
                  {/* autoComplete="email", NOT "username": "username" reads the password
                      manager's credential store, which on this origin is permanently
                      empty because no password is ever submitted for it to save. "email"
                      is the contact-info store, which is populated independently.
                      name/id are required too — Chrome needs a stable field identity.

                      ...plus the `webauthn` token, which is a DIFFERENT store again and the
                      reason the above still holds: it tells the browser to offer this
                      operator's PASSKEYS in the same dropdown. It must come last in the
                      list, and it does nothing at all unless a conditional ceremony is
                      running to attach to (see the effect above). */}
                  <input id="signin-email" name="email" type="email" autoComplete="email webauthn" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@tkt.ug" className={inputCls} autoFocus />
                </div>
                <button type="submit" disabled={!email.trim() || !!busy} className={primaryBtn(!email.trim() || busy)}>
                  {busy === 'send' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  {busy === 'send' ? 'Sending code…' : 'Send sign-in code'}
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-1">
                <KeyRound className="w-4 h-4 text-[#4B5563]" strokeWidth={1.75} />
                <h1 className="text-[16px] font-semibold text-[#1F2937]">Enter your code</h1>
              </div>
              <p className="text-[12.5px] text-[#6B7280] mb-4">{info || `We sent a code to ${email.trim()}.`}</p>

              {error && <ErrorBox>{error}</ErrorBox>}

              <form onSubmit={verify} className="space-y-3">
                <div>
                  <label className="block text-[11.5px] font-medium text-[#1F2937] mb-1.5">Sign-in code</label>
                  <input ref={otpRef} inputMode="numeric" autoComplete="one-time-code" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="123456" className={`${inputCls} tracking-[0.3em] text-center font-mono text-[16px]`} />
                </div>
                <button type="submit" disabled={otp.trim().length < 4 || !!busy} className={primaryBtn(otp.trim().length < 4 || busy)}>
                  {busy === 'verify' ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  {busy === 'verify' ? 'Verifying…' : 'Verify & sign in'}
                </button>
              </form>

              <div className="flex items-center justify-between mt-3 text-[11.5px]">
                <button onClick={() => { setStep('email'); setOtp(''); setError(''); setInfo(''); }} className="text-[#6B7280] hover:text-[#1F2937] inline-flex items-center gap-1"><ArrowLeft className="w-3 h-3" />Change email</button>
                <button onClick={resend} disabled={!!busy} className="text-[#1F2937] hover:underline disabled:opacity-50">{busy === 'resend' ? 'Resending…' : 'Resend code'}</button>
              </div>
            </>
          )}

          {DEV_AUTH_AVAILABLE && (
            <>
              <div className="flex items-center gap-3 my-4"><div className="h-px flex-1 bg-[#EEEDEA]" /><span className="text-[10.5px] uppercase tracking-[0.06em] text-[#9CA3AF]">local dev</span><div className="h-px flex-1 bg-[#EEEDEA]" /></div>
              <button onClick={devLogin} disabled={!!busy} className="w-full h-9 rounded-md text-[12.5px] font-medium border border-[#E6E4E1] bg-white hover:bg-[#FBFAF7] text-[#1F2937] inline-flex items-center justify-center gap-2 disabled:opacity-50">
                {busy === 'dev' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Terminal className="w-3.5 h-3.5" />}
                {busy === 'dev' ? 'Signing in…' : 'Continue as developer'}
              </button>
            </>
          )}
        </div>
        <div className="text-center text-[11px] text-[#9CA3AF] mt-4">{BRAND.name} Platform · authorized access only</div>
      </div>
    </div>
  );
};

const ErrorBox = ({ children }) => (
  <div className="flex items-start gap-2 bg-[#FEF2F2] border border-[#FECACA] rounded-md px-3 py-2 mb-4 text-[12px] text-[#7F1D1D]">
    <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" strokeWidth={2} />
    <span>{children}</span>
  </div>
);
